import React from 'react';
import { MotionConfig } from 'framer-motion';

/**
 * Shared scroll-reveal variants for the CustomSections.
 *
 * Use with motion.* components:
 *   <motion.header variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce}>
 *
 * `once: true` plays the animation the first time the element scrolls into
 * view and keeps it visible afterwards. `MotionSection` disables transform
 * animation for users who prefer reduced motion (opacity is kept).
 *
 * Easing: [0.22, 1, 0.36, 1] is a smooth deceleration curve (easeOutExpo-ish)
 * that feels editorial and unhurried — no bounce, no harsh snap.
 */

// Custom editorial cubic-bezier: fast start, soft landing.
const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: EASE },
  },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.9, ease: EASE },
  },
};

export const fadeInFromLeft = {
  hidden: { opacity: 0, x: -32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.75, ease: EASE },
  },
};

export const fadeInFromRight = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.75, ease: EASE },
  },
};

export const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.12 },
  },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: EASE },
  },
};

/**
 * Viewport config: trigger once when 12% of the element is visible.
 * 12% (vs the previous 15%) fires the reveal a touch earlier so content
 * is already animating as it enters the natural reading zone.
 */
export const viewportOnce = { once: true, amount: 0.12 };

/**
 * Wrapper that makes scroll animations respect `prefers-reduced-motion`.
 * Rendering only a context provider (no extra DOM node).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MotionSection = ({ children }) => (
  <MotionConfig reducedMotion="user">{children}</MotionConfig>
);
